import * as React from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCw } from "lucide-react";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleReset = () => {
    this.props.onReset?.();
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          onRetry={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

interface ErrorFallbackProps {
  error: Error | null;
  onRetry?: () => void;
  className?: string;
}

export function ErrorFallback({
  error,
  onRetry,
  className,
}: ErrorFallbackProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center min-h-[400px] px-4 text-center",
        className
      )}
    >
      <div className="h-12 w-12 rounded-full bg-[#f5f5f5] flex items-center justify-center mb-4">
        <AlertCircle className="h-6 w-6 text-[#000000]" />
      </div>
      <h2 className="text-lg font-semibold text-[#000000] mb-2">
        Something went wrong
      </h2>
      <p className="text-sm text-[#666666] max-w-md mb-2">
        We encountered an error while loading this content.
      </p>
      {error?.message && (
        <p className="text-xs text-[#999999] max-w-md mb-6 font-mono bg-[#f5f5f5] px-3 py-2 rounded">
          {error.message}
        </p>
      )}
      {onRetry && (
        <Button onClick={onRetry} variant="outline">
          <RefreshCw className="mr-2 h-4 w-4" />
          Try again
        </Button>
      )}
    </div>
  );
}

export default ErrorBoundary;
